// Force password change page — shown on first login when must_change_password is true
// User must set a new password meeting strength requirements before accessing the app

import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { authService } from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';
import { Eye, EyeOff, Lock, Check, X } from 'lucide-react';
import toast from 'react-hot-toast';

const ChangePassword: React.FC = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const { t } = useLanguage();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // If not authenticated, redirect to login
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  // If user doesn't need to change password, redirect to home
  if (!user.mustChangePassword) {
    if (user.role === 'S' || user.role === 'supervisor') {
      return <Navigate to="/supervisor" replace />;
    }
    return <Navigate to="/" replace />;
  }

  // Password strength checks
  const checks = {
    length: newPassword.length >= 8,
    uppercase: /[A-Z]/.test(newPassword),
    lowercase: /[a-z]/.test(newPassword),
    digit: /[0-9]/.test(newPassword),
    special: /[!@#$%^&*]/.test(newPassword),
    match: newPassword === confirmPassword && confirmPassword.length > 0,
  };

  const allValid = Object.values(checks).every(Boolean);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!allValid) return;

    setIsSubmitting(true);
    try {
      await authService.forceChangePassword(newPassword, confirmPassword);
      toast.success(t('changePassword.success'));
      // Force re-login so user object gets updated without mustChangePassword
      logout();
    } catch (error: any) {
      const message = error.response?.data?.message || t('common.error');
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const CheckItem = ({ ok, label }: { ok: boolean; label: string }) => (
    <div className={`flex items-center gap-2 text-sm ${ok ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}`}>
      {ok ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />}
      {label}
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mb-4">
            <Lock className="h-8 w-8 text-primary-600 dark:text-primary-400" />
          </div>
          <h2 className="text-2xl font-bold text-secondary-900 dark:text-white">
            {t('changePassword.title')}
          </h2>
          <p className="mt-2 text-sm text-secondary-600 dark:text-secondary-400">
            {t('changePassword.subtitle')}
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {/* New Password */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
              {t('changePassword.newPassword')}
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full input-field pr-10"
                placeholder={t('changePassword.newPasswordPlaceholder')}
                autoFocus
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff className="h-4 w-4 text-secondary-400" /> : <Eye className="h-4 w-4 text-secondary-400" />}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
              {t('changePassword.confirmPassword')}
            </label>
            <div className="relative">
              <input
                type={showConfirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full input-field pr-10"
                placeholder={t('changePassword.confirmPasswordPlaceholder')}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowConfirm(!showConfirm)}
              >
                {showConfirm ? <EyeOff className="h-4 w-4 text-secondary-400" /> : <Eye className="h-4 w-4 text-secondary-400" />}
              </button>
            </div>
          </div>

          {/* Password Requirements */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-2">
            <p className="text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
              {t('changePassword.requirements')}
            </p>
            <CheckItem ok={checks.length} label={t('changePassword.minLength')} />
            <CheckItem ok={checks.uppercase} label={t('changePassword.uppercase')} />
            <CheckItem ok={checks.lowercase} label={t('changePassword.lowercase')} />
            <CheckItem ok={checks.digit} label={t('changePassword.digit')} />
            <CheckItem ok={checks.special} label={t('changePassword.special')} />
            <CheckItem ok={checks.match} label={t('changePassword.passwordsMatch')} />
          </div>

          <button
            type="submit"
            disabled={!allValid || isSubmitting}
            className="w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
          >
            {isSubmitting ? t('common.loading') : t('changePassword.submit')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChangePassword;
